import {
  Flex,
  Icon,
  NumberInput,
  NumberInputField,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineInformationCircle } from "react-icons/hi2";

import { FormControl } from "../../../components";
import { AiInterviewQuestion, AiInterviewQuestionType } from "../../graphql";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type QuestionWeightInputProps = {
  index: number;
  questions: AiInterviewQuestion[];
  onBlur?: () => void;
};

export const QuestionWeightInput: React.FC<QuestionWeightInputProps> = ({
  index,
  questions,
  onBlur,
}) => {
  const {
    register,
    formState: { errors },
  } = useAiInterviewerFormContext();

  const scoringQuestions = questions.filter(
    (q) => q.type === AiInterviewQuestionType.Score
  );

  const sumOtherCustomWeights = (): number =>
    questions.reduce((acc, q, i) => {
      if (i === index) return acc;
      if (q.type !== AiInterviewQuestionType.Score) return acc;
      return acc + (q.customWeight ?? 0);
    }, 0);

  return (
    <FormControl error={errors.questions?.[index]?.customWeight}>
      <Tooltip
        label={
          <>
            Scored questions are equally weighted by default to provide an
            overall interview score. All score weightings must equal 100%.
          </>
        }
        openDelay={300}
      >
        <Flex
          display="inline-flex"
          align="center"
          gap="1"
          mb="1"
          color="gray.600"
          fontSize="sm"
        >
          Score weight
          <Icon as={HiOutlineInformationCircle} boxSize="4" color="gray.500" />
        </Flex>
      </Tooltip>
      <Flex align="center" gap={2}>
        <NumberInput w="14" bg="white">
          <NumberInputField
            {...register(`questions.${index}.customWeight`, {
              onBlur: () => {
                onBlur?.();
              },
              validate: (value) => {
                if (value === undefined || value === null) return;

                if (value <= 0) return "Weight must be greater than 0";
                if (value >= 100) return "Weight must be less than 100";

                const otherSum = sumOtherCustomWeights();
                if (otherSum + value > 100)
                  return "The sum of all weights must be 100%";
                return true;
              },
              setValueAs: (value) => {
                if (typeof value === "number") return value;
                if (value?.trim?.()) {
                  const number = Number(value);
                  if (!Number.isNaN(number)) return number;
                }
                return undefined;
              },
            })}
            onInput={(e) => {
              const el = e.target as HTMLInputElement;
              const { value } = el;

              // NumberInput handles a lot of input validation,
              // but it still allows minus, decimal, and "e".
              // We want to restrict to integer.
              if (value.match(/[^0-9]/)) {
                const repl = value.replace(/[^0-9].*/, "");
                el.value = repl;
              }
            }}
            placeholder={(() => {
              const remainingWeight = Math.max(
                100 - sumOtherCustomWeights(),
                0
              );
              const otherUnset = scoringQuestions.filter(
                (q) => !q.customWeight
              );
              const numUnset = otherUnset.length;
              const floor = Math.floor(remainingWeight / numUnset);
              const remainder = remainingWeight % numUnset;
              if (otherUnset.indexOf(questions[index]) < remainder) {
                return `${floor + 1}`;
              }
              return `${floor}`;
            })()}
            pr="3"
            pl="3"
            py="2"
          />
        </NumberInput>
        <Text fontSize="sm" color="gray.900">
          %
        </Text>
      </Flex>
    </FormControl>
  );
};
